import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Pill, Calendar, Download, FileSpreadsheet } from 'lucide-react';
import Authenticated from '../../layout/Authenticated';
import axiosInstance from '../../api/axiosInstance';
import * as XLSX from 'xlsx';

const MedicineWiseProfitReport = () => {
    const currentDate = new Date();
    const [filterType, setFilterType] = useState('date_range'); // 'medicine', 'month', 'year', 'date_range'
    const [selectedMedicine, setSelectedMedicine] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [fromDate, setFromDate] = useState(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
    const [medicines, setMedicines] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
        { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
        { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);

    // Fetch medicines for dropdown
    useEffect(() => {
        const fetchMedicines = async () => {
            try {
                const response = await axiosInstance.get('/medicine/search?limit=1000');
                setMedicines(response.data.medicines || []);
            } catch (err) {
                console.error('Error fetching medicines:', err);
            }
        };
        fetchMedicines();
    }, []);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = { filterType };

            if (filterType === 'medicine') {
                params.medicineId = selectedMedicine;
            } else if (filterType === 'month') {
                params.month = selectedMonth;
                params.year = selectedYear;
            } else if (filterType === 'year') {
                params.year = selectedYear;
            } else if (filterType === 'date_range') {
                params.startDate = fromDate;
                params.endDate = toDate;
            }

            const response = await axiosInstance.get('/reports/medicine-profit', { params });
            setReportData(response.data.data);
        } catch (err) {
            console.error('Error fetching report:', err);
            setError('Failed to load report. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [filterType, selectedMedicine, selectedMonth, selectedYear, fromDate, toDate]);

    // Fetch report based on filter type
    useEffect(() => {
        if (filterType === 'medicine' && !selectedMedicine) return;
        fetchReport();
    }, [fetchReport, filterType, selectedMedicine]);

    const downloadExcel = () => {
        if (!reportData || !reportData.medicines || reportData.medicines.length === 0) return;

        const workbook = XLSX.utils.book_new();

        // Main Report Data
        const reportRows = [
            ['MEDICINE-WISE PROFIT REPORT'],
            [`Filter: ${filterType.replace('_', ' ').toUpperCase()}`],
            [`Period: ${reportData.period || ''}`],
            [''],
            ['Medicine Name', 'Manufacturer', 'Current Stock (Units)', 'Current Stock (Pkg)', 'Purchase Qty', 'Purchase Amt (₹)', 'Avg Purchase Price', 'Sales Qty', 'Sales Amt (₹)', 'Avg Selling Price', 'Profit (₹)', 'Profit %', 'Margin/Unit', 'Margin %']
        ];

        reportData.medicines.forEach(med => {
            reportRows.push([
                med.medicine_name,
                med.manufacturer || 'N/A',
                med.current_stock_units,
                parseFloat(med.current_stock_packages),
                med.total_purchased_quantity,
                parseFloat(med.total_purchase_amount),
                parseFloat(med.avg_purchase_price),
                med.total_sold_quantity,
                parseFloat(med.total_sales_amount),
                parseFloat(med.avg_selling_price),
                parseFloat(med.profit),
                parseFloat(med.profit_percentage),
                parseFloat(med.profit_margin_per_unit),
                parseFloat(med.profit_margin_percentage)
            ]);
        });

        // Add summary row
        reportRows.push([]);
        reportRows.push(['TOTAL', '',
            reportData.medicines.reduce((sum, m) => sum + parseInt(m.current_stock_units || 0), 0),
            '',
            reportData.medicines.reduce((sum, m) => sum + parseInt(m.total_purchased_quantity || 0), 0),
            reportData.medicines.reduce((sum, m) => sum + parseFloat(m.total_purchase_amount || 0), 0),
            '',
            reportData.medicines.reduce((sum, m) => sum + parseInt(m.total_sold_quantity || 0), 0),
            reportData.medicines.reduce((sum, m) => sum + parseFloat(m.total_sales_amount || 0), 0),
            '',
            reportData.medicines.reduce((sum, m) => sum + parseFloat(m.profit || 0), 0),
            '',
            '',
            ''
        ]);

        const mainSheet = XLSX.utils.aoa_to_sheet(reportRows);

        // Set column widths
        mainSheet['!cols'] = [
            { wch: 30 }, // Medicine Name
            { wch: 20 }, // Manufacturer
            { wch: 12 }, // Stock Units
            { wch: 12 }, // Stock Pkg
            { wch: 12 }, // Purchase Qty
            { wch: 15 }, // Purchase Amount
            { wch: 15 }, // Avg Purchase Price
            { wch: 12 }, // Sales Qty
            { wch: 15 }, // Sales Amount
            { wch: 15 }, // Avg Selling Price
            { wch: 15 }, // Profit
            { wch: 12 }, // Profit %
            { wch: 12 }, // Margin/Unit
            { wch: 12 }  // Margin %
        ];

        XLSX.utils.book_append_sheet(workbook, mainSheet, 'Medicine Profit Report');

        // Generate filename based on filter
        let filename = 'medicine_profit_report';
        if (filterType === 'medicine') {
            const medName = medicines.find(m => m.id === parseInt(selectedMedicine))?.name || 'medicine';
            filename = `${medName.replace(/\s+/g, '_')}_profit_report`;
        } else if (filterType === 'month') {
            filename = `medicine_profit_${months.find(m => m.value === selectedMonth)?.label}_${selectedYear}`;
        } else if (filterType === 'year') {
            filename = `medicine_profit_${selectedYear}`;
        } else if (filterType === 'date_range') {
            filename = `medicine_profit_${fromDate}_to_${toDate}`;
        }

        XLSX.writeFile(workbook, `${filename}.xlsx`);
    };

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-4 px-3 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6 border border-purple-100">
                        <div className="bg-gradient-to-r from-purple-600 to-purple-500 p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex items-center space-x-3">
                                    <div className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-xl">
                                        <FileSpreadsheet className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                                    </div>
                                    <div>
                                        <h1 className="text-xl sm:text-2xl font-bold text-white">Medicine-Wise Profit Report</h1>
                                        <p className="text-purple-100 text-xs sm:text-sm mt-1">Purchase vs Sales Analysis</p>
                                    </div>
                                </div>

                                {reportData && reportData.medicines && reportData.medicines.length > 0 && (
                                    <button
                                        onClick={downloadExcel}
                                        className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/30 rounded-lg text-white backdrop-blur-sm hover:bg-white/20 transition-all"
                                    >
                                        <Download className="w-4 h-4" />
                                        <span>Export Excel</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-200">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Filter Options</h3>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                            <button
                                onClick={() => setFilterType('medicine')}
                                className={`p-4 rounded-xl border-2 transition-all ${filterType === 'medicine'
                                    ? 'border-purple-500 bg-purple-50'
                                    : 'border-gray-200 hover:border-purple-300'
                                    }`}
                            >
                                <Pill className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                                <p className="text-sm font-semibold">By Medicine</p>
                            </button>

                            <button
                                onClick={() => setFilterType('month')}
                                className={`p-4 rounded-xl border-2 transition-all ${filterType === 'month'
                                    ? 'border-purple-500 bg-purple-50'
                                    : 'border-gray-200 hover:border-purple-300'
                                    }`}
                            >
                                <Calendar className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                                <p className="text-sm font-semibold">By Month</p>
                            </button>

                            <button
                                onClick={() => setFilterType('year')}
                                className={`p-4 rounded-xl border-2 transition-all ${filterType === 'year'
                                    ? 'border-purple-500 bg-purple-50'
                                    : 'border-gray-200 hover:border-purple-300'
                                    }`}
                            >
                                <TrendingUp className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                                <p className="text-sm font-semibold">By Year</p>
                            </button>

                            <button
                                onClick={() => setFilterType('date_range')}
                                className={`p-4 rounded-xl border-2 transition-all ${filterType === 'date_range'
                                    ? 'border-purple-500 bg-purple-50'
                                    : 'border-gray-200 hover:border-purple-300'
                                    }`}
                            >
                                <Calendar className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                                <p className="text-sm font-semibold">Date Range</p>
                            </button>
                        </div>

                        {/* Filter Inputs */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {filterType === 'medicine' && (
                                <select
                                    value={selectedMedicine}
                                    onChange={(e) => setSelectedMedicine(e.target.value)}
                                    className="px-4 py-2 border-2 border-gray-200 rounded-lg outline-none focus:border-purple-500"
                                >
                                    <option value="">Select Medicine</option>
                                    {medicines.map(med => (
                                        <option key={med.id} value={med.id}>
                                            {med.name} - {med.manufacturer}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {filterType === 'month' && (
                                <>
                                    <select
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                        className="px-4 py-2 border-2 border-gray-200 rounded-lg outline-none focus:border-purple-500"
                                    >
                                        {months.map(month => (
                                            <option key={month.value} value={month.value}>{month.label}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={selectedYear}
                                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                        className="px-4 py-2 border-2 border-gray-200 rounded-lg outline-none focus:border-purple-500"
                                    >
                                        {years.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </>
                            )}

                            {filterType === 'year' && (
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                    className="px-4 py-2 border-2 border-gray-200 rounded-lg outline-none focus:border-purple-500"
                                >
                                    {years.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            )}

                            {filterType === 'date_range' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">From Date</label>
                                        <input
                                            type="date"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg outline-none focus:border-purple-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">To Date</label>
                                        <input
                                            type="date"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg outline-none focus:border-purple-500"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-6">
                            <p className="text-red-700">{error}</p>
                        </div>
                    )}

                    {/* Report Table */}
                    {reportData && reportData.medicines && reportData.medicines.length > 0 && !loading && (
                        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-purple-600 text-white">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-sm font-semibold">#</th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold">Medicine Name</th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold">Manufacturer</th>
                                            <th className="px-4 py-3 text-center text-sm font-semibold">Stock (Units)</th>
                                            <th className="px-4 py-3 text-center text-sm font-semibold">Purchase Qty</th>
                                            <th className="px-4 py-3 text-right text-sm font-semibold">Avg Purchase Price</th>
                                            <th className="px-4 py-3 text-center text-sm font-semibold">Sales Qty</th>
                                            <th className="px-4 py-3 text-right text-sm font-semibold">Avg Selling Price</th>
                                            <th className="px-4 py-3 text-right text-sm font-semibold">Profit Margin/Unit</th>
                                            <th className="px-4 py-3 text-center text-sm font-semibold">Margin %</th>
                                            <th className="px-4 py-3 text-right text-sm font-semibold">Total Profit</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {reportData.medicines.map((med, index) => (
                                            <tr key={med.medicine_id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                                                <td className="px-4 py-3 text-sm font-semibold text-gray-900">{med.medicine_name}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600">{med.manufacturer || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-center">
                                                    <span className="text-blue-700 font-semibold">{med.current_stock_units}</span>
                                                    <span className="text-xs text-gray-500 block">({med.current_stock_packages} pkg)</span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-center text-gray-900">{med.total_purchased_quantity}</td>
                                                <td className="px-4 py-3 text-sm text-right text-gray-900">
                                                    ₹{parseFloat(med.avg_purchase_price).toFixed(2)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-center text-gray-900">{med.total_sold_quantity}</td>
                                                <td className="px-4 py-3 text-sm text-right text-gray-900">
                                                    ₹{parseFloat(med.avg_selling_price).toFixed(2)}
                                                </td>
                                                <td className={`px-4 py-3 text-sm text-right font-semibold ${parseFloat(med.profit_margin_per_unit) >= 0 ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                    ₹{parseFloat(med.profit_margin_per_unit).toFixed(2)}
                                                </td>
                                                <td className={`px-4 py-3 text-sm text-center font-semibold ${parseFloat(med.profit_margin_percentage) >= 0 ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                    {parseFloat(med.profit_margin_percentage).toFixed(2)}%
                                                </td>
                                                <td className={`px-4 py-3 text-sm text-right font-bold ${parseFloat(med.profit) >= 0 ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                    ₹{parseFloat(med.profit).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-100 font-bold">
                                        <tr>
                                            <td colSpan="3" className="px-4 py-3 text-sm text-gray-900">TOTAL</td>
                                            <td className="px-4 py-3 text-sm text-center text-blue-700">
                                                {reportData.medicines.reduce((sum, m) => sum + parseInt(m.current_stock_units || 0), 0)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-center text-gray-900">
                                                {reportData.medicines.reduce((sum, m) => sum + parseInt(m.total_purchased_quantity || 0), 0)}
                                            </td>
                                            <td></td>
                                            <td className="px-4 py-3 text-sm text-center text-gray-900">
                                                {reportData.medicines.reduce((sum, m) => sum + parseInt(m.total_sold_quantity || 0), 0)}
                                            </td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td className="px-4 py-3 text-sm text-right text-green-600">
                                                ₹{reportData.medicines.reduce((sum, m) => sum + parseFloat(m.profit || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}

                    {reportData && reportData.medicines && reportData.medicines.length === 0 && !loading && (
                        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-200">
                            <FileSpreadsheet className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 text-lg">No data found for selected filter</p>
                        </div>
                    )}
                </div>
            </div>
        </Authenticated>
    );
};

export default MedicineWiseProfitReport;
